import { useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import styled from 'styled-components';
import { message } from 'antd';
import { useQuery } from '@tanstack/react-query';

import useTranslation from 'customHooks/translate';
import {
  getCountries,
  getBusinessTypes,
  getLegalDocTypes,
} from 'services/collections';
import { queryClient } from 'config/query-client';
import moment from 'moment-timezone';
import {
  createDebitor,
  createCreditor,
  getCreditorrById,
  getDebitorrById,
  updateCreditor,
  updateDebitor,
} from 'services/partners';
import { getAccountData } from 'services/accounts';
import { NewPartnerCustomError } from 'types';
import {
  ACCOUNT_INFO,
  BUSINESS_TYPES,
  COUNTRIES,
  CREDITORS,
  DEBITORS,
  PARTNER,
} from 'events/types';

import CustomDialog from './CustomDialog';

const initialValues = {
  name: '',
  email: '',
  businessTypeId: null,
  isCreditor: null,
  street: '',
  city: '',
  zip: '',
  legalDocTypeId: null,
  legalDocNumber: '',
  countryId: null,
  contractNumber: '',
  termOfDue: null,
  directorName: '',
  contractDate: '',
  remark: '',
};

const AddNewPartnerForm = (props) => {
  const { open, onPartnerChange, onSuccess, isCreditor, disabled } = props;
  const fieldPlaceholder = (text: string) => (disabled ? '-' : text);
  const [nameError, setNameError] = useState<string | null>(null);
  const [cityError, setCityError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [legalDocNumberError, setLegalDocNumberError] = useState<string | null>(
    null
  );
  const [streetError, setStreetError] = useState<string | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const [termOfDueError, setTermOfDueError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [contractDateError, setContractDateError] = useState<string | null>(
    null
  );
  const [remarkError, setRemarkError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);

  const translation = useTranslation();

  const [partner, setPartner] = useState(initialValues);

  // Helper function to check if Azerbaijan is selected
  const isAzerbaijanSelected = () => {
    const selectedCountry = countriesQuery.data?.find(
      (c) => c.value === partner.countryId
    );
    return (
      selectedCountry?.label?.toLowerCase().includes('azerbaijan') ||
      selectedCountry?.label?.toLowerCase().includes('azərbaycan')
    );
  };

  const partnerEditId = props.partnerToEdit?.id;
  const canLoadPartnerById =
    partnerEditId !== undefined &&
    partnerEditId !== null &&
    partnerEditId !== '';

  useQuery(
    [PARTNER, partnerEditId],
    () => {
      const pathname = window.location.pathname;
      if (pathname.endsWith('/creditors')) {
        return getCreditorrById(partnerEditId);
      }
      return getDebitorrById(partnerEditId);
    },
    {
      enabled: open && canLoadPartnerById,
      staleTime: 0,
      onSuccess: (response) => {
        setPartner(response.data.data);
      },
    }
  );

  const setOnValueChange = (event, propName, isSelect = false) => {
    event.persist && event.persist();
    const value = isSelect ? event.value : event.target.value;

    setPartner((prevState) => ({
      ...prevState,
      [propName]: value,
    }));

    if (propName === 'name') setNameError(null);
    if (propName === 'city') setCityError(null);
    if (propName === 'countryId') setCountryError(null);
    if (propName === 'legalDocNumber') setLegalDocNumberError(null);
    if (propName === 'street') setStreetError(null);
    if (propName === 'zip') setZipError(null);
    if (propName === 'email') setEmailError(null);
    if (propName === 'termOfDue') setTermOfDueError(null);
    if (propName === 'contractDate') setContractDateError(null);
    if (propName === 'remark') setRemarkError(null);
  };

  useEffect(() => {
    if (partner.businessTypeId) {
      getLegalDocTypes(partner.businessTypeId).then((legalDocType) => {
        const docTypes = legalDocType.data.data.map((type) => ({
          value: type.legalDocTypeId,
          label: type.legalDocTypeName,
        }));

        if (docTypes.length > 0) {
          setOnValueChange(
            {
              value: docTypes[0].value,
              label: docTypes[0].label,
            },
            'legalDocTypeId',
            true
          );
        }
      });
    }
  }, [partner.businessTypeId]);

  const handleSave = (event) => {
    event.preventDefault();
    setLoading(true);
    const {
      name,
      email,
      businessTypeId,
      street,
      city,
      zip,
      legalDocTypeId,
      legalDocNumber,
      countryId,
      contractNumber,
      contractDate,
      termOfDue,
      directorName,
      remark,
    } = partner;
    const updatedBusinessTypeId = businessTypeId || null;
    const updatedLegalDocTypeId = legalDocTypeId || null;
    const updatedCountryId = countryId || null;
    const partnerData = {
      id: props.partnerToEdit?.id,
      name,
      email,
      businessTypeId: updatedBusinessTypeId,
      isCreditor: isCreditor ? true : false,
      street,
      city,
      zip,
      legalDocTypeId: updatedLegalDocTypeId,
      legalDocNumber,
      countryId: updatedCountryId,
      contractNumber,
      contractDate: contractDate?.trim()
        ? contractDate?.split('T')[0]
        : null,
      termOfDue,
      directorName,
      remark,
    };

    const method = props.partnerToEdit?.id
      ? isCreditor
        ? updateCreditor
        : updateDebitor
      : isCreditor
      ? createCreditor
      : createDebitor;
    method(partnerData)
      .then(() => {
        message.success(
          props.partnerToEdit?.id
            ? `${translation.messages.successAdded}`
            : `${translation.messages.successUpdated}`
        );

        if (onSuccess) {
          onSuccess();
        }

        if (onPartnerChange) {
          onPartnerChange();
        }

        queryClient.invalidateQueries({
          queryKey: [isCreditor ? CREDITORS : DEBITORS],
          refetchType: 'all',
        });
        setPartner(initialValues);
        props.handleClose();
      })
      .catch((error: NewPartnerCustomError) => {
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        const errorMessageName = error.response?.data.errors?.Name?.[0] as
          | string
          | undefined;
        const errorMessageCity = error.response?.data.errors?.City?.[0] as
          | string
          | undefined;
        const errorMessageCountry = error.response?.data.errors
          ?.CountryId?.[0] as string | undefined;
        const errorMessageLegalDocNumber = error.response?.data.errors
          ?.LegalDocNumber?.[0] as string | undefined;
        const errorMessageStreet = error.response?.data.errors?.Street?.[0] as
          | string
          | undefined;
        const errorMessageZip = error.response?.data.errors?.Zip?.[0] as
          | string
          | undefined;
        const errorMessageTermOfDue = error.response?.data.errors
          ?.TermOfDue?.[0] as string | undefined;
        const errorMessageRemark = error.response?.data.errors?.Remark?.[0] as
          | string
          | undefined;
        const errorMessageEmail = error.response?.data.errors?.Email?.[0] as
          | string
          | undefined;
        const errorMessageContractDate = error.response?.data.errors
          ?.ContractDate?.[0] as string | undefined;

        setNameError(errorMessageName || null);
        setCityError(errorMessageCity || null);
        setCountryError(errorMessageCountry || null);
        setLegalDocNumberError(errorMessageLegalDocNumber || null);
        setStreetError(errorMessageStreet || null);
        setZipError(errorMessageZip || null);
        setTermOfDueError(errorMessageTermOfDue || null);
        setRemarkError(errorMessageRemark || null);
        setEmailError(errorMessageEmail || null);
        setContractDateError(errorMessageContractDate || null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const countriesQuery = useQuery({
    queryKey: [COUNTRIES],
    queryFn: getCountries,
    select: (response) =>
      response.data.data.map((c) => ({ value: c.id, label: c.name })),
  });

  useEffect(() => {
    if (!props.partnerToEdit && countriesQuery.data?.length > 0) {
      const firstCountry = countriesQuery.data[0];
      setOnValueChange(
        { value: firstCountry.value, label: firstCountry.label },
        'countryId',
        true
      );
    }
  }, [props.partnerToEdit, countriesQuery.data]);

  const businessTypesQuery = useQuery({
    queryKey: [BUSINESS_TYPES],
    queryFn: getBusinessTypes,
    select: (response) =>
      response.data.data.map((type) => ({
        value: type.businessTypeId,
        label: type.businessTypeName,
      })),
  });

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
  });

  useEffect(() => {
    if (!props.partnerToEdit) {
      setPartner((prevState) => ({
        ...prevState,
      }));
    }
  }, [props.partnerToEdit]);

  useEffect(() => {
    if (accountInfoQuery.data?.timeZoneId) {
      const updatedDate = moment()
        .tz(accountInfoQuery.data.timeZoneId)
        .format('YYYY-MM-DD');
      setPartner((prev) => ({
        ...prev,
        invoiceDate: updatedDate,
      }));
    }
  }, [accountInfoQuery.data?.timeZoneId]);

  useEffect(() => {
    if (!open) {
      setPartner(initialValues);
      setNameError(null);
      setCityError(null);
      setCountryError(null);
      setLegalDocNumberError(null);
      setStreetError(null);
      setZipError(null);
      setTermOfDueError(null);
      setContractDateError(null);
      setRemarkError(null);
      setEmailError(null);
    }
  }, [open]);

  return (
    <CustomDialog
      isLoading={loading}
      onSubmit={handleSave}
      open={props.open}
      handleClose={props.handleClose}
      title={
        props.partnerToEdit?.id
          ? translation.partners.UpdatePartner
          : translation.partners.AddNewButton
      }
      buttonTitle={
        props.partnerToEdit?.id
          ? translation.partners.UpdatePartner
          : translation.partners.AddNewButton
      }
      disabled={disabled}
    >
      <form className="flex flex-col gap-[20px]">
        <div className="flex flex-col gap-[12px]">
          <label
            id="alert-dialog-slide-description"
            htmlFor="name"
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
          >
            {translation.creditorAdd.Name} *
          </label>
          <Input
            type="text"
            maxLength={100}
            onChange={(event) => setOnValueChange(event, 'name')}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder={fieldPlaceholder(translation.creditorAdd.Name)}
            value={partner.name || ''}
            style={{
              borderColor: nameError ? 'red' : '',
            }}
            disabled={disabled}
          />
          {nameError && (
            <span className="text-[12px] text-red-600">{nameError}</span>
          )}
        </div>

        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="countryId"
          >
            {translation.creditorAdd.Country} *
          </label>
          <Select
            isDisabled={disabled}
            placeholder={fieldPlaceholder('')}
            options={countriesQuery.data}
            onChange={(event) => setOnValueChange(event, 'countryId', true)}
            value={countriesQuery.data?.find(
              (countryQuery) => countryQuery.value === partner.countryId
            )}
            id="countryId"
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                height: '48px',
                borderRadius: 6,
                borderColor: countryError ? 'red' : '',
              }),
            }}
          />
          {countryError && (
            <span className="text-[12px] text-red-600">{countryError}</span>
          )}
        </div>
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="street"
          >
            {translation.creditorAdd.Street} *
          </label>
          <Input
            disabled={disabled}
            onChange={(event) => setOnValueChange(event, 'street')}
            value={partner.street || ''}
            className="d-block"
            id="street"
            type="text"
            maxLength={100}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder={fieldPlaceholder(translation.creditorAdd.Street)}
            style={{
              borderColor: streetError ? 'red' : '',
            }}
          />
          {streetError && (
            <span className="text-[12px] text-red-600">{streetError}</span>
          )}
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
          <div className="flex flex-col gap-[12px]">
            <label
              className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
              htmlFor="city"
            >
              {translation.creditorAdd.City}
            </label>
            <Input
              disabled={disabled}
              onChange={(event) => setOnValueChange(event, 'city')}
              value={partner.city || ''}
              id="city"
              type="text"
              maxLength={50}
              placeholder={fieldPlaceholder('Cape Town')}
              style={{
                borderColor: cityError ? 'red' : '',
              }}
            />
            {cityError && (
              <span className="text-[12px] text-red-600">{cityError}</span>
            )}
          </div>
          <div className="flex flex-col gap-[12px]">
            <label
              className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
              htmlFor="zip"
            >
              {translation.creditorAdd.Zip}
            </label>
            <Input
              disabled={disabled}
              onChange={(event) => setOnValueChange(event, 'zip')}
              value={partner.zip || ''}
              id="zip"
              type="text"
              maxLength={9}
              placeholder={fieldPlaceholder('AZ1111')}
              style={{
                borderColor: zipError ? 'red' : '',
              }}
            />
            {zipError && (
              <span className="text-[12px] text-red-600">{zipError}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="email"
          >
            {translation.creditorAdd.Email}
          </label>
          <Input
            disabled={disabled}
            onChange={(event) => setOnValueChange(event, 'email')}
            value={partner.email || ''}
            className="d-block"
            id="email"
            type="text"
            maxLength={39}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder={fieldPlaceholder('john@mail.com')}
            style={{
              borderColor: emailError ? 'red' : '',
            }}
          />
          {emailError && (
            <span className="text-[12px] text-red-600">{emailError}</span>
          )}
        </div>
        <div className="flex flex-col gap-[12px]">
          <label
            htmlFor="legalDocNumber"
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
          >
            {translation.info.tin} {isAzerbaijanSelected() ? '*' : ''}
          </label>
          <Input
            disabled={disabled}
            onChange={(event) => setOnValueChange(event, 'legalDocNumber')}
            value={partner.legalDocNumber || ''}
            className="d-block"
            id="legalDocNumber"
            type="text"
            maxLength={39}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder={fieldPlaceholder('123652358')}
            style={{
              borderColor: legalDocNumberError ? 'red' : '',
            }}
          />
          {legalDocNumberError && (
            <span className="text-[12px] text-red-600">
              {legalDocNumberError}
            </span>
          )}
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
          <div className="flex flex-col gap-[12px]">
            <label
              className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
              htmlFor="contractNumber"
            >
              {translation.creditorAdd.ContractNumber}
            </label>
            <Input
              disabled={disabled}
              onChange={(event) => setOnValueChange(event, 'contractNumber')}
              value={partner.contractNumber || ''}
              id="contractNumber"
              type="text"
              maxLength={20}
              pattern="[+-]?\d+(?:[.,]\d+)?"
              placeholder={fieldPlaceholder('987652314')}
            />
          </div>
          <div className="flex flex-col gap-[12px]">
            <label
              className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
              htmlFor="contractDate"
            >
              {translation.creditorAdd.ContractDate}
            </label>
            <Input
              disabled={disabled}
              onChange={(event) => {
                setOnValueChange(event, 'contractDate');
              }}
              value={partner.contractDate?.split('T')[0] || ''}
              id="contractDate"
              type="date"
              style={{
                borderColor: contractDateError ? 'red' : '',
              }}
            />
            {contractDateError && (
              <span className="text-[12px] text-red-600">
                {contractDateError}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
          <div className="flex flex-col gap-[12px]">
            <label
              id="alert-dialog-slide-description"
              className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
              htmlFor="name"
            >
              {translation.creditorAdd.DirectorName}
            </label>
            <Input
              disabled={disabled}
              type="text"
              maxLength={100}
              onChange={(event) => setOnValueChange(event, 'directorName')}
              pattern="[+-]?\d+(?:[.,]\d+)?"
              placeholder={fieldPlaceholder(
                translation.creditorAdd.DirectorName
              )}
              value={partner.directorName || ''}
            />
          </div>
          <div className="flex flex-col gap-[12px]">
            <label
              className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
              htmlFor="termOfDue"
            >
              {translation.creditorAdd.TermOfDue}
            </label>
            <Input
              disabled={disabled}
              onChange={(event) => setOnValueChange(event, 'termOfDue')}
              onFocus={
                disabled ? undefined : (event) => (event.target.value = '')
              }
              placeholder={fieldPlaceholder(translation.creditorAdd.TermOfDue)}
              value={
                disabled ? partner.termOfDue ?? '' : partner.termOfDue || 0
              }
              id="termOfDue"
              type="number"
              min={0}
              style={{
                borderColor: termOfDueError ? 'red' : '',
              }}
            />
            {termOfDueError && (
              <span className="text-[12px] text-red-600">{termOfDueError}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="remark"
          >
            {translation.creditorAdd.Remark}
          </label>
          <Textarea
            disabled={disabled}
            id="remark"
            placeholder={fieldPlaceholder(translation.creditorAdd.Remark)}
            value={partner.remark || ''}
            onChange={(event) => {
              setOnValueChange(event, 'remark');
            }}
          />
          {remarkError && (
            <span className="text-[12px] text-red-600">{remarkError}</span>
          )}
        </div>
      </form>
    </CustomDialog>
  );
};

export default AddNewPartnerForm;

const Input = styled.input`
  width: 100%;
  border-radius: 6px;
  padding: 12.7px 15px;
  border: 1px solid rgba(41, 45, 50, 0.25);
  :focus {
    outline: none !important;
    border: 2px solid #466afb;
    padding: 11.9px 14px;
  }
  &:disabled {
    background-color: hsl(0, 0%, 95%);
    border-color: #e5e7eb;
    color: hsl(0, 0%, 60%);
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  border-radius: 6px;
  padding: 12.7px 15px;
  border: 1px solid rgba(41, 45, 50, 0.25);
  resize: vertical;
  :focus {
    outline: none !important;
    border: 2px solid #466afb;
    padding: 11.9px 14px;
  }
  &:disabled {
    background-color: hsl(0, 0%, 95%);
    border-color: #e5e7eb;
    color: hsl(0, 0%, 60%);
    cursor: not-allowed;
  }
`;

const Select = styled(ReactSelect)`
  & .react-select__value-container {
    padding: 8.7px 16px;
  }
`;
